import React from 'react';

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  className?: string;
}

interface AdminTableProps<T> {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  loading?: boolean;
  error?: string | null;
  emptyText?: string;
  page: number;
  totalPages: number;
  onPageChange: (next: number) => void;
}

export function AdminTable<T>({
  rows,
  columns,
  rowKey,
  loading,
  error,
  emptyText = 'Žádné záznamy',
  page,
  totalPages,
  onPageChange,
}: AdminTableProps<T>) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {error && <div className="p-4 bg-red-50 border-b border-red-200 text-sm text-red-700">{error}</div>}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map(col => (
                <th key={col.key} className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${col.className ?? ''}`}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading && (
              <tr>
                <td colSpan={columns.length} className="px-4 py-6 text-center text-sm text-gray-500">
                  Načítám…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="px-4 py-6 text-center text-sm text-gray-500">
                  {emptyText}
                </td>
              </tr>
            )}
            {!loading &&
              rows.map(row => (
                <tr key={rowKey(row)} className="hover:bg-gray-50">
                  {columns.map(col => (
                    <td key={col.key} className={`px-4 py-3 text-sm text-gray-700 ${col.className ?? ''}`}>
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
          <button
            className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50"
            disabled={page === 0 || loading}
            onClick={() => onPageChange(Math.max(0, page - 1))}
          >
            Předchozí
          </button>
          <span className="text-sm text-gray-700">
            Strana {page + 1} z {totalPages}
          </span>
          <button
            className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50"
            disabled={page + 1 >= totalPages || loading}
            onClick={() => onPageChange(page + 1)}
          >
            Další
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminTable;
